/*  iceberg-ui  */
import {
    Box,
    Flex,
    IconWithVariant,
    IconWithVariantProps
} from '@auviknetworks/iceberg-ui';

/*  Shared  */
import { FontSizes } from 'application/Shared/Constants';

/*  Yarn  */
import { differenceInDays } from 'date-fns';
import i18n from 'i18n';

/*  React  */
import React from 'react';

/*  internal  */
import { DarkTooltip } from '../Tooltips';

const getDate = ({ date }: { date: Date }): string => {
    const options: Intl.DateTimeFormatOptions = {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric'
    };

    // This locale was chosen so that it returns yyyy/mm/dd
    return date.toLocaleDateString('en-ZA', options);
};

const getAbbreviatedMonthDate = ({ date }: { date: Date }): string => {
    const options: Intl.DateTimeFormatOptions = {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    };

    return date.toLocaleDateString(undefined, options);
};

const getTime = ({ date, noTz }: { date: Date; noTz: boolean }): string =>
    date.toLocaleTimeString(undefined, noTz ? {} : { timeZoneName: 'short' });

export const getDateTime = ({
    abbreviatedMonthDate = false,
    date: propsDate,
    noTimeString = false,
    noTz = false
}: {
    date: string | number | Date;
    noTimeString?: boolean;
    noTz?: boolean;
    abbreviatedMonthDate?: boolean;
}): string => {
    if (!propsDate) {
        return i18n.t('Common.unavailable');
    }
    const date = new Date(propsDate);
    const dateDate = abbreviatedMonthDate
        ? getAbbreviatedMonthDate({ date })
        : getDate({ date });
    const time = getTime({ date, noTz });

    return !noTimeString ? `${dateDate} ${time}` : `${dateDate}`;
};

export const DateFormat = ({
    abbreviatedMonthDate,
    alertInactivity,
    date,
    noTimeString,
    noTz,
    small,
    tooltipIcon,
    tooltipText
}: {
    abbreviatedMonthDate?: boolean;
    alertInactivity?: number;
    date: null | string | number | Date;
    noTimeString?: boolean;
    noTz?: boolean;
    small?: boolean;
    tooltipIcon?: IconWithVariantProps['src'];
    tooltipText?: string;
}) => {
    if (!date) {
        return <>-</>;
    }

    const includeAlert =
        !!alertInactivity &&
        alertInactivity > 0 &&
        differenceInDays(new Date(), new Date(date)) > alertInactivity;

    const dateTime = getDateTime({
        abbreviatedMonthDate,
        date,
        noTimeString,
        noTz
    });

    return (
        <Flex alignItems="center">
            <Box mr="8px">
                <DateTime small={small} date={dateTime} />
            </Box>
            {includeAlert && (
                <DarkTooltip arrow title={tooltipText}>
                    <Flex alignItems="center">
                        <IconWithVariant
                            variant="danger"
                            src={tooltipIcon || 'Clock'}
                            size={FontSizes[1]}
                        />
                    </Flex>
                </DarkTooltip>
            )}
        </Flex>
    );
};

const DateTime = ({ small, date }: { small?: boolean; date: string }) =>
    // eslint-disable-next-line react/jsx-no-useless-fragment
    small ? <small>{date}</small> : <>{date}</>;
